import React, { useEffect, useState } from 'react'
import api from '../api/axios'
import { CreditCard, RefreshCw, CheckCircle, Clock, Zap } from 'lucide-react'

export default function BillingPage() {
  const [billing, setBilling] = useState<any>(null)
  const [plans, setPlans] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [subscribing, setSubscribing] = useState(false)
  const [addonLoading, setAddonLoading] = useState(false)
  const [error, setError] = useState('')
  const [addonError, setAddonError] = useState('')
  const [expandedPlanId, setExpandedPlanId] = useState<string | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponResult, setCouponResult] = useState<any>(null)
  const [couponError, setCouponError] = useState('')
  const [applyingCoupon, setApplyingCoupon] = useState(false)

  const fetchBilling = () => {
    return Promise.all([
      api.get('/payment/billing-info'),
      api.get('/payment/plans')
    ]).then(([billingRes, plansRes]) => {
      setBilling(billingRes.data)
      const paypalPlans = plansRes.data.filter((p: any) => p.price && Number(p.price) > 0)
      setPlans(paypalPlans)
      if (billingRes.data.lastPlan) {
        setSelectedPlanId(billingRes.data.lastPlan.planId)
      } else if (paypalPlans.length > 0) {
        setSelectedPlanId(paypalPlans[0].planId)
      }
    }).catch(() => {})
  }

  useEffect(() => {
    fetchBilling().finally(() => setLoading(false))
  }, [])

  const handleSubscribe = async () => {
    if (!selectedPlanId) return
    setSubscribing(true)
    setError('')
    try {
      const res = await api.post('/payment/create-order', {
        planId: selectedPlanId,
        isNewClient: false,
        couponCode: couponResult ? couponCode : undefined
      })
      window.location.href = res.data.approvalUrl
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to initiate payment')
      setSubscribing(false)
    }
  }

  const handleBuyAddon = async () => {
  setAddonLoading(true)
  setAddonError('')
  try {
    console.log('Token:', localStorage.getItem('token'))
    console.log('User:', localStorage.getItem('user'))
    const res = await api.post('/payment/create-addon-order')
    window.location.href = res.data.approvalUrl
  } catch (e: any) {
    setAddonError(e.response?.data?.error || 'Failed to initiate add-on payment')
    setAddonLoading(false)
  }
}

const handleApplyCoupon = async () => {
  if (!couponCode.trim()) return
  setApplyingCoupon(true)
  setCouponError('')
  setCouponResult(null)
  try {
    const res = await api.post('/coupons/validate', {
      code: couponCode,
      planId: selectedPlanId
    })
    setCouponResult(res.data)
  } catch (e: any) {
    setCouponError(e.response?.data?.error || 'Invalid coupon code')
  }
  setApplyingCoupon(false)
}

  if (loading) return <div className="p-4 sm:p-8 text-center text-gray-400">Loading...</div>

  const activeSub = billing?.activeSub
  const hasAddon = activeSub?.addonPrice && Number(activeSub.addonPrice) > 0
  const addonActive = activeSub?.revisionTopupActive

  return (
    <div className="p-4 sm:p-8 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-navy-900 dark:text-white">Billing</h1>
        <p className="text-gray-400 mt-1 text-sm">Manage your subscription and payment history</p>
      </div>

      {/* Current subscription */}
      <div className="card p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-9 h-9 bg-orange-50 dark:bg-orange-900/20 rounded-xl flex items-center justify-center">
            <CreditCard size={18} className="text-orange-500" />
          </div>
          <h2 className="font-display font-semibold text-navy-900 dark:text-white">Current Subscription</h2>
        </div>

        {activeSub ? (
          <div className="space-y-4">
            {/* Plan name + status row */}
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className="font-semibold text-navy-800 dark:text-white">{activeSub.planName}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {new Date(activeSub.startDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} —{' '}
                  {new Date(activeSub.endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="px-3 py-1 bg-green-50 dark:bg-green-900/20 text-green-600 text-xs font-semibold rounded-full flex items-center gap-1">
                  <CheckCircle size={11} /> Active
                </span>

                {/* ── Addon badge or button ── */}
                {hasAddon && (
                  addonActive ? (
                    <span className="px-3 py-1 bg-purple-50 dark:bg-purple-900/20 text-purple-600 text-xs font-semibold rounded-full flex items-center gap-1">
                      <Zap size={11} /> Revision Add-on Active
                    </span>
                  ) : (
                    <button
                      onClick={handleBuyAddon}
                      disabled={addonLoading}
                      className="px-3 py-1 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white text-xs font-semibold rounded-full flex items-center gap-1 transition-colors"
                    >
                      <Zap size={11} />
                      {addonLoading ? 'Redirecting...' : `Buy Revision Add-on — $${Number(activeSub.addonPrice).toFixed(0)}`}
                    </button>
                  )
                )}
              </div>
            </div>

            {/* Addon error */}
            {addonError && (
              <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg">{addonError}</p>
            )}

            {/* Usage bar */}
            <div className="space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Jobs used</span>
                <span className="font-medium text-navy-700 dark:text-gray-200">{activeSub.jobsUsed} / {activeSub.totalJobsAllowed}</span>
              </div>
              <div className="w-full bg-gray-100 dark:bg-[#1a3050] rounded-full h-2">
                <div className="bg-orange-500 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (activeSub.jobsUsed / activeSub.totalJobsAllowed) * 100)}%` }} />
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span className="flex items-center gap-1"><Clock size={10} /> {activeSub.daysLeft} days remaining</span>
                {activeSub.unlimitedRevisions || addonActive ? (
                  <span className="text-purple-500 font-medium">Unlimited Revisions</span>
                ) : activeSub.freeRevisions > 0 ? (
                  <span>{activeSub.revisionsUsed} / {activeSub.freeRevisions} free revisions used</span>
                ) : null}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-400 text-sm mb-2">No active subscription</p>
            <p className="text-xs text-gray-500">Choose a plan below to get started</p>
          </div>
        )}
      </div>

      {/* Renew / Subscribe */}
      <div className="card p-6">
        <h2 className="font-display font-semibold text-navy-900 dark:text-white mb-4 flex items-center gap-2">
          <RefreshCw size={16} className="text-orange-500" />
          {activeSub ? 'Renew or Change Plan' : 'Subscribe'}
        </h2>

        {billing?.lastPlan && !activeSub && (
          <div className="p-3 bg-orange-50 dark:bg-orange-900/10 border border-orange-100 dark:border-orange-900/30 rounded-xl mb-4">
            <p className="text-xs text-orange-600 dark:text-orange-400">
              Your last plan was <strong>{billing.lastPlan.planName}</strong> — you can resubscribe to it or choose a different one.
            </p>
          </div>
        )}

        <div className="space-y-3 mb-5">
          {plans.map(p => (
  <div key={p.planId}
    className={`border-2 rounded-xl overflow-hidden transition-all ${selectedPlanId === p.planId ? 'border-orange-500' : 'border-gray-100 dark:border-[#1e3a5f] hover:border-orange-200'}`}>
    {/* Main row */}
    <div
      onClick={() => setSelectedPlanId(p.planId)}
      className={`p-4 cursor-pointer flex items-center justify-between ${selectedPlanId === p.planId ? 'bg-orange-50 dark:bg-orange-900/10' : ''}`}>
      <div>
        <div className="flex items-center gap-2">
          <p className="font-semibold text-navy-800 dark:text-white text-sm">{p.planName}</p>
          {p.isPopular && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">Popular</span>}
        </div>
        <p className="text-xs text-gray-400 mt-0.5">{p.jobCount} jobs · {p.periodDays} days</p>
      </div>
      <div className="flex items-center gap-3">
        <p className="text-orange-500 font-bold text-lg">${p.price}<span className="text-xs text-gray-400 font-normal">/{p.periodDays <= 7 ? 'wk' : 'mo'}</span></p>
        <button
          onClick={e => { e.stopPropagation(); setExpandedPlanId(expandedPlanId === p.planId ? null : p.planId) }}
          className="text-xs text-gray-400 hover:text-orange-500 transition-colors">
          {expandedPlanId === p.planId ? '▲' : '▼'}
        </button>
      </div>
    </div>
    {/* Expandable summary */}
    {expandedPlanId === p.planId && (
      <div className="px-4 pb-4 pt-2 bg-gray-50 dark:bg-[#0d1e35] border-t border-gray-100 dark:border-[#1e3a5f] space-y-2">
        {p.description && <p className="text-xs text-gray-500 dark:text-gray-400">{p.description}</p>}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="text-green-500">✓</span> {p.jobCount} jobs per period
          </div>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="text-green-500">✓</span> {p.periodDays} day{p.periodDays > 1 ? 's' : ''} validity
          </div>
          <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
            <span className="text-green-500">✓</span> {p.unlimitedRevisions ? 'Unlimited revisions' : `${p.freeRevisions || 0} free revisions`}
          </div>
          {p.complexJobsAllowed > 0 && (
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
              <span className="text-green-500">✓</span> {p.complexJobsAllowed} complex jobs
            </div>
          )}
          {p.addonPrice > 0 && (
            <div className="flex items-center gap-2 text-purple-500 col-span-2">
              <span>⚡</span> Revision add-on available for ${Number(p.addonPrice).toFixed(0)}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
))}
        </div>

        {/* Coupon Code */}
        <div className="mb-4">
          <div className="flex gap-2">
            <input
              className="input text-sm flex-1 font-mono uppercase"
              placeholder="Coupon code (optional)"
              value={couponCode}
              onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponResult(null); setCouponError('') }}
            />
            <button onClick={handleApplyCoupon} disabled={applyingCoupon || !couponCode.trim()}
              className="text-xs px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold rounded-xl transition-colors flex-shrink-0">
              {applyingCoupon ? '...' : 'Apply'}
            </button>
          </div>
          {couponResult && (
            <p className="text-xs text-green-600 mt-1.5">✓ {couponResult.name} — {couponResult.discountType === 'percentage' ? `${couponResult.discountValue}% off` : `$${couponResult.discountValue} off`}</p>
          )}
          {couponError && <p className="text-xs text-red-500 mt-1.5">{couponError}</p>}
        </div>

        {error && <p className="text-xs text-red-500 bg-red-50 dark:bg-red-900/20 px-3 py-2 rounded-lg mb-3">{error}</p>}

        <button onClick={handleSubscribe} disabled={subscribing || !selectedPlanId}
          className="w-full bg-[#0070ba] hover:bg-[#005ea6] disabled:opacity-50 text-white font-bold py-3 rounded-xl transition-colors flex items-center justify-center gap-2">
          {subscribing ? 'Redirecting to PayPal...' : '🔒 Pay with PayPal'}
        </button>
        <p className="text-xs text-gray-400 text-center mt-2">Secured by PayPal. Recurring billing — cancel anytime.</p>
      </div>

      {/* Transaction history */}
      {billing?.transactions?.length > 0 && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-[#1e3a5f]">
            <h2 className="font-display font-semibold text-navy-900 dark:text-white">Payment History</h2>
          </div>
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[400px]">
            <thead className="bg-gray-50/70 dark:bg-[#0d1e35]">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase">Plan</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-400 uppercase">Amount</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-400 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-[#1a3050]">
              {billing.transactions.map((t: any) => (
                <tr key={t.id}>
                  <td className="px-6 py-3 text-navy-800 dark:text-gray-200">{t.planName || '—'}</td>
                  <td className="px-6 py-3 text-right font-mono text-navy-700 dark:text-gray-300">${t.amount}</td>
                  <td className="px-6 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.status === 'completed' ? 'bg-green-50 text-green-600' : t.status === 'cancelled' ? 'bg-red-50 text-red-400' : 'bg-yellow-50 text-yellow-600'}`}>
                      {t.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-xs text-gray-400">
                    {new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </div>
  )
}